import type { DateTime } from 'luxon';

import { getTenantUnits } from '@core/configuration';
import {
  DISCOUNT_AMOUNT_DECIMALS,
  DISCOUNT_AMOUNT_ROUNDING_TOLERANCE,
  PERCENTAGE_DISCOUNT_LIMIT,
} from '@core/constants/discounts';
import type { ImportedDiscount } from '@core/types/api/ImportedDiscount/ImportedDiscount';
import {
  NewQuoteProductDiscount,
  type QuoteProductDiscount,
} from '@core/types/api/QuoteProductDiscount/QuoteProductDiscount';
import { FormatTenantDisplayDateTime } from '@core/utils/dateHelpers';
import { cleanedDecimal } from '@core/utils/decimals';
import { hasValue } from '@core/utils/equality';
import { formatCurrency } from '@core/utils/format';
import { pluralize } from '@core/utils/pluralize';
import { NIL_UUID, isValidUUID } from '@core/utils/uuid';

export const DiscountAmountType = {
  Fixed: 'fixed',
  Percentage: 'percentage',
} as const;

export const formatDiscountAmount = (amount: string, amountType: string): string =>
  amountType === DiscountAmountType.Percentage
    ? `${amount}%`
    : formatCurrency(amount, getTenantUnits().currencyCode);

export const discountAmountTypeLabel = (amountType: string): string =>
  amountType === DiscountAmountType.Percentage ? 'Percentage' : 'Fixed';

export const discountValidityLabel = (validTo: DateTime | null): string =>
  hasValue(validTo)
    ? `Valid until ${FormatTenantDisplayDateTime(validTo, { month: 'short', day: 'numeric', year: 'numeric' })}`
    : '';

export const discountOfferLabel = (
  discount: ImportedDiscount,
  currencySymbol: string,
  unitLabel: string,
): string => {
  if (discount.amountType === DiscountAmountType.Percentage) {
    return `-${discount.amount}%`;
  }
  const amount = parseFloat(cleanedDecimal(discount.amount) || '0') || 0;
  return `-${currencySymbol}${amount.toFixed(2)}${unitLabel}`;
};

export const discountButtonLabel = (appliedCount: number): string =>
  appliedCount === 0 ? 'Discount' : `Discount: ${appliedCount} applied`;

export const discountProductsLabel = (productCount: number): string =>
  productCount === 0 ? 'All Products' : `${productCount} ${pluralize(productCount, 'Product')}`;

export const formatDiscountValidityDate = (value: DateTime | null, openLabel: string): string =>
  hasValue(value)
    ? FormatTenantDisplayDateTime(value, { month: '2-digit', day: '2-digit', year: 'numeric' })
    : openLabel;

export const discountAmountOff = (
  amount: string,
  amountType: string,
  grossPrice: number,
): number => {
  const value = parseFloat(cleanedDecimal(amount) || '0');
  if (Number.isNaN(value)) {
    return 0;
  }

  return amountType === DiscountAmountType.Percentage ? (grossPrice * value) / 100 : value;
};

export const totalDiscountOff = (applied: readonly QuoteProductDiscount[]): number =>
  applied.reduce((sum, d) => {
    const value = parseFloat(cleanedDecimal(d.appliedAmount) || '0');
    return Number.isNaN(value) ? sum : sum + value;
  }, 0);

export const netPrice = (applied: readonly QuoteProductDiscount[], grossPrice: number): number =>
  grossPrice - totalDiscountOff(applied);

export const dropsNetPriceBelowZero = (
  applied: readonly QuoteProductDiscount[],
  grossPrice: number,
): boolean => parseFloat(netPrice(applied, grossPrice).toFixed(DISCOUNT_AMOUNT_DECIMALS)) < 0;

export const settledNetPrice = (
  applied: readonly QuoteProductDiscount[],
  grossPrice: number,
): number =>
  dropsNetPriceBelowZero(applied, grossPrice)
    ? netPrice(applied, grossPrice)
    : Math.max(0, netPrice(applied, grossPrice));

export const parsedAmount = (value: string | null | undefined): number =>
  parseFloat(cleanedDecimal(value) || '0') || 0;

export const amountOffOf = (discount: QuoteProductDiscount): number =>
  parsedAmount(discount.appliedAmount);

export const appliedWithinNetPriceFloor = (
  applied: readonly QuoteProductDiscount[],
  grossPrice: number,
): QuoteProductDiscount[] => {
  let kept = [...applied];
  while (kept.length > 0 && dropsNetPriceBelowZero(kept, grossPrice)) {
    const largest = kept.reduce((a, b) =>
      amountOffOf(b) > amountOffOf(a) ||
      (amountOffOf(b) === amountOffOf(a) && b.name.localeCompare(a.name) < 0)
        ? b
        : a,
    );
    kept = kept.filter((d) => d !== largest);
  }
  return kept;
};

export const grossPriceOf = (
  applied: readonly QuoteProductDiscount[],
  netUnitPrice: number,
): number => netUnitPrice + totalDiscountOff(applied);

export const appliedFromImported = (
  discount: ImportedDiscount,
  grossPrice: number,
): QuoteProductDiscount =>
  NewQuoteProductDiscount({
    importedDiscountId: discount.id,
    name: discount.name,
    amountType: discount.amountType,
    appliedAmount: discountAmountOff(discount.amount, discount.amountType, grossPrice).toFixed(
      DISCOUNT_AMOUNT_DECIMALS,
    ),
    stackable: discount.stackable,
  });

const percentOf = (discount: ImportedDiscount | undefined): number =>
  discount?.amountType === DiscountAmountType.Percentage
    ? parseFloat(cleanedDecimal(discount.amount) || '0') || 0
    : 0;

export type SavedShares = ReadonlyMap<string, number>;

export const savedPercentShares = (
  applied: readonly QuoteProductDiscount[],
  netUnitPrice: number,
): Map<string, number> => {
  const grossPrice = grossPriceOf(applied, netUnitPrice);
  const shares = new Map<string, number>();
  if (grossPrice <= 0) {
    return shares;
  }
  for (const a of applied) {
    if (a.amountType === DiscountAmountType.Percentage && isValidUUID(a.id)) {
      shares.set(a.id, (amountOffOf(a) / grossPrice) * 100);
    }
  }
  return shares;
};

export const savedPercentSharesOfRows = (
  rows: readonly { price?: { number: string } | null; discounts?: QuoteProductDiscount[] }[],
): Map<string, number> => {
  const shares = new Map<string, number>();
  for (const row of rows) {
    const net = parseFloat(cleanedDecimal(row.price?.number ?? '') || '0') || 0;
    for (const [id, share] of savedPercentShares(row.discounts ?? [], net)) {
      shares.set(id, share);
    }
  }
  return shares;
};

const savedPercentOf = (
  applied: QuoteProductDiscount,
  currentGrossPrice: number,
  savedShares?: SavedShares,
): number =>
  savedShares?.get(applied.id) ??
  (currentGrossPrice > 0 ? (amountOffOf(applied) / currentGrossPrice) * 100 : 0);

export const appliedPercentage = (
  applied: readonly QuoteProductDiscount[],
  matching: readonly ImportedDiscount[],
  currentGrossPrice = 0,
  savedShares?: SavedShares,
): number =>
  applied.reduce((sum, a) => {
    const source = matching.find((m) => m.id === a.importedDiscountId);
    if (hasValue(source)) {
      return sum + percentOf(source);
    }
    return a.amountType === DiscountAmountType.Percentage && isSavedUnoffered(a, matching)
      ? sum + savedPercentOf(a, currentGrossPrice, savedShares)
      : sum;
  }, 0);

export const reachesPercentageLimit = (
  candidate: ImportedDiscount,
  applied: readonly QuoteProductDiscount[],
  matching: readonly ImportedDiscount[],
  currentGrossPrice = 0,
  savedShares?: SavedShares,
): boolean =>
  percentOf(candidate) > 0 &&
  appliedPercentage(applied, matching, currentGrossPrice, savedShares) + percentOf(candidate) >=
    PERCENTAGE_DISCOUNT_LIMIT;

export const discountPicksKey = (applied: readonly QuoteProductDiscount[]): string =>
  applied
    .map((discount) => discount.importedDiscountId)
    .sort()
    .join('|');

export const hasSameDiscountPicks = (
  first: readonly QuoteProductDiscount[],
  second: readonly QuoteProductDiscount[],
): boolean =>
  first.length === second.length &&
  first.every((discount) =>
    second.some((other) => other.importedDiscountId === discount.importedDiscountId),
  );

export const hasSameAppliedDiscounts = (
  first: readonly QuoteProductDiscount[],
  second: readonly QuoteProductDiscount[],
): boolean =>
  first.length === second.length &&
  first.every((discount) =>
    second.some(
      (other) =>
        other.importedDiscountId === discount.importedDiscountId &&
        Math.abs(amountOffOf(other) - amountOffOf(discount)) < DISCOUNT_AMOUNT_ROUNDING_TOLERANCE,
    ),
  );

export const matchesRowContext = (
  applied: QuoteProductDiscount,
  matching: readonly ImportedDiscount[],
): boolean => matching.some((d) => d.id === applied.importedDiscountId);

export const isSavedUnoffered = (
  applied: QuoteProductDiscount,
  matching: readonly ImportedDiscount[],
): boolean => isValidUUID(applied.id) && !matchesRowContext(applied, matching);

const stackableOf = (
  applied: QuoteProductDiscount,
  matching: readonly ImportedDiscount[],
): boolean =>
  matching.find((m) => m.id === applied.importedDiscountId)?.stackable ??
  (isSavedUnoffered(applied, matching) && applied.stackable);

const canCombine = (
  stackable: boolean,
  applied: readonly QuoteProductDiscount[],
  matching: readonly ImportedDiscount[],
): boolean => applied.length === 0 || (stackable && applied.every((a) => stackableOf(a, matching)));

export const canAddDiscount = (
  candidate: ImportedDiscount,
  applied: readonly QuoteProductDiscount[],
  matching: readonly ImportedDiscount[],
): boolean => canCombine(candidate.stackable, applied, matching);

export const grossPriceFromNet = (
  applied: readonly QuoteProductDiscount[],
  matching: readonly ImportedDiscount[],
  netUnitPrice: number,
  currentGrossPrice = 0,
  savedShares?: SavedShares,
): number => {
  let fixedOff = 0;
  let percentOff = 0;
  for (const a of applied) {
    const source = matching.find((m) => m.id === a.importedDiscountId);
    if (source?.amountType === DiscountAmountType.Percentage) {
      percentOff += parseFloat(cleanedDecimal(source.amount) || '0') || 0;
    } else if (
      !hasValue(source) &&
      a.amountType === DiscountAmountType.Percentage &&
      isSavedUnoffered(a, matching)
    ) {
      percentOff += savedPercentOf(a, currentGrossPrice, savedShares);
    } else {
      fixedOff += parseFloat(cleanedDecimal(source?.amount ?? a.appliedAmount) || '0') || 0;
    }
  }
  if (percentOff >= 100) {
    return netUnitPrice + fixedOff;
  }
  return (netUnitPrice + fixedOff) / (1 - percentOff / 100);
};

const withAmountOff = (
  applied: QuoteProductDiscount,
  amountOff: number,
  id: string = NIL_UUID,
): QuoteProductDiscount => {
  const currentAmountOff = parseFloat(cleanedDecimal(applied.appliedAmount) || '0') || 0;
  if (Math.abs(amountOff - currentAmountOff) < DISCOUNT_AMOUNT_ROUNDING_TOLERANCE) {
    return applied;
  }
  return NewQuoteProductDiscount({
    ...applied,
    id,
    appliedAmount: amountOff.toFixed(DISCOUNT_AMOUNT_DECIMALS),
  });
};

export const reappliedAtGross = (
  applied: readonly QuoteProductDiscount[],
  matching: readonly ImportedDiscount[],
  grossPrice: number,
  currentGrossPrice = grossPrice,
  savedShares?: SavedShares,
): QuoteProductDiscount[] =>
  applied.map((a) => {
    const source = matching.find((m) => m.id === a.importedDiscountId);
    if (!hasValue(source)) {
      if (a.amountType !== DiscountAmountType.Percentage || !isSavedUnoffered(a, matching)) {
        return a;
      }
      const share = savedPercentOf(a, currentGrossPrice, savedShares);
      return share > 0 ? withAmountOff(a, (grossPrice * share) / 100, a.id) : a;
    }
    const isRepricedPercentage =
      source.amountType === DiscountAmountType.Percentage &&
      grossPrice !== currentGrossPrice &&
      isValidUUID(a.id);
    const refreshed = withAmountOff(
      a,
      discountAmountOff(source.amount, source.amountType, grossPrice),
    );
    const rematched =
      isRepricedPercentage && refreshed === a
        ? NewQuoteProductDiscount({ ...a, id: NIL_UUID })
        : refreshed;
    if (a.stackable === source.stackable && a.amountType === source.amountType) {
      return rematched;
    }
    return NewQuoteProductDiscount({
      ...rematched,
      id: NIL_UUID,
      stackable: source.stackable,
      amountType: source.amountType,
    });
  });

export const rescaledToGrossPrice = (
  applied: readonly QuoteProductDiscount[],
  netUnitPrice: number,
  grossPrice: number,
): QuoteProductDiscount[] => {
  const currentGrossPrice = grossPriceOf(applied, netUnitPrice);
  return applied.map((a) => {
    if (a.amountType !== DiscountAmountType.Percentage || currentGrossPrice <= 0) {
      return a;
    }
    return withAmountOff(a, (amountOffOf(a) * grossPrice) / currentGrossPrice, a.id);
  });
};

// Re-derives the gross from a typed net using only the stored amounts, for clients that don't
// fetch the current offers; each percentage keeps the share of the gross it had.
export const rescaledToNetPrice = (
  applied: readonly QuoteProductDiscount[],
  currentNetPrice: number,
  netUnitPrice: number,
  savedShares?: SavedShares,
): QuoteProductDiscount[] => {
  const currentGrossPrice = grossPriceOf(applied, currentNetPrice);
  const shareOf = (a: QuoteProductDiscount): number =>
    a.amountType === DiscountAmountType.Percentage
      ? savedPercentOf(a, currentGrossPrice, savedShares) / 100
      : 0;
  let fixedOff = 0;
  let percentShare = 0;
  for (const a of applied) {
    if (a.amountType === DiscountAmountType.Percentage) {
      percentShare += shareOf(a);
    } else {
      fixedOff += amountOffOf(a);
    }
  }
  if (percentShare >= 1) {
    return [...applied];
  }
  const grossPrice = (netUnitPrice + fixedOff) / (1 - percentShare);
  return applied.map((a) =>
    a.amountType === DiscountAmountType.Percentage && shareOf(a) > 0
      ? withAmountOff(a, grossPrice * shareOf(a), a.id)
      : a,
  );
};

export enum ExceedingDiscounts {
  Keep = 'Keep',
  Unapply = 'Unapply',
}

// Re-prices a line to a new gross. Keep leaves a line alone when its discounts would take the
// net below zero (the reset contract); Unapply drops the largest until the net fits.
export const repricedAtGross = (
  applied: readonly QuoteProductDiscount[],
  currentNetPrice: number,
  grossPrice: number,
  onExceeding: ExceedingDiscounts,
): { discounts: QuoteProductDiscount[]; netPrice: number } | null => {
  const rescaled = rescaledToGrossPrice(applied, currentNetPrice, grossPrice);
  if (!dropsNetPriceBelowZero(rescaled, grossPrice)) {
    return { discounts: rescaled, netPrice: settledNetPrice(rescaled, grossPrice) };
  }
  if (onExceeding === ExceedingDiscounts.Keep) {
    return null;
  }
  const kept = appliedWithinNetPriceFloor(rescaled, grossPrice);
  return { discounts: kept, netPrice: settledNetPrice(kept, grossPrice) };
};

export const pendingDiscounts = (
  applied: readonly QuoteProductDiscount[],
): QuoteProductDiscount[] =>
  applied.map((d) => (isValidUUID(d.id) ? NewQuoteProductDiscount({ ...d, id: NIL_UUID }) : d));

export const isSelectionCombinable = (
  selected: readonly QuoteProductDiscount[],
  matching: readonly ImportedDiscount[],
): boolean =>
  selected.every((d) => {
    const source = matching.find((m) => m.id === d.importedDiscountId);
    return (
      hasValue(source) &&
      canAddDiscount(
        source,
        selected.filter((other) => other !== d),
        matching,
      )
    );
  });

export const hasPendingDiscounts = (applied: readonly QuoteProductDiscount[]): boolean =>
  applied.some((d) => !isValidUUID(d.id));

// The picks that are still offered and may sit together under the current stacking and
// percentage rules. Every price derivation starts from this set so the picker agrees with it.
export const combinableDiscounts = (
  applied: readonly QuoteProductDiscount[],
  matching: readonly ImportedDiscount[],
  currentGrossPrice = 0,
  savedShares?: SavedShares,
): QuoteProductDiscount[] => {
  const kept: QuoteProductDiscount[] = [];
  for (const discount of applied.filter((d) => isSavedUnoffered(d, matching))) {
    if (canCombine(discount.stackable, kept, matching)) {
      kept.push(discount);
    }
  }
  for (const discount of applied.filter((d) => matchesRowContext(d, matching))) {
    const source = matching.find((m) => m.id === discount.importedDiscountId);
    if (
      hasValue(source) &&
      canAddDiscount(source, kept, matching) &&
      !reachesPercentageLimit(source, kept, matching, currentGrossPrice, savedShares)
    ) {
      kept.push(discount);
    }
  }

  return applied.filter((d) => kept.includes(d));
};

export const resolveDiscountsAtGross = (
  applied: readonly QuoteProductDiscount[],
  matching: readonly ImportedDiscount[],
  grossPrice: number,
  currentGrossPrice = grossPrice,
  savedShares?: SavedShares,
): { discounts: QuoteProductDiscount[]; belowZero: QuoteProductDiscount[] } => {
  const combinable = reappliedAtGross(
    combinableDiscounts(applied, matching, currentGrossPrice, savedShares),
    matching,
    grossPrice,
    currentGrossPrice,
    savedShares,
  );
  const discounts = appliedWithinNetPriceFloor(combinable, grossPrice);

  return { discounts, belowZero: combinable.filter((d) => !discounts.includes(d)) };
};

export const resolveDiscountsAtNet = (
  applied: readonly QuoteProductDiscount[],
  matching: readonly ImportedDiscount[],
  netUnitPrice: number,
  currentGrossPrice = 0,
  savedShares?: SavedShares,
): QuoteProductDiscount[] =>
  reappliedAtNet(
    combinableDiscounts(applied, matching, currentGrossPrice, savedShares),
    matching,
    netUnitPrice,
    currentGrossPrice,
    savedShares,
  );

export const reappliedAtNet = (
  applied: readonly QuoteProductDiscount[],
  matching: readonly ImportedDiscount[],
  netUnitPrice: number,
  currentGrossPrice = 0,
  savedShares?: SavedShares,
): QuoteProductDiscount[] => {
  const grossPrice = grossPriceFromNet(
    applied,
    matching,
    netUnitPrice,
    currentGrossPrice,
    savedShares,
  );
  return reappliedAtGross(
    applied,
    matching,
    grossPrice,
    currentGrossPrice > 0 ? currentGrossPrice : grossPrice,
    savedShares,
  );
};
